import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { X, Search, ChevronDown, Loader2 } from "lucide-react";
import { base_url } from "../../../config/config";

const ITEMS_PER_PAGE = 20;

// ANR Selection Modal with Infinite Scroll
export const ANRSelectionModal = ({ open, onClose, onSelect }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [projects, setProjects] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalProjects, setTotalProjects] = useState(0);

  const listRef = useRef(null);
  const debounceTimeoutRef = useRef(null);

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 10 }, (_, i) => currentYear - i);

  const fetchProjects = useCallback(
    async (pageNum = 1, isNewSearch = false) => {
      if (isNewSearch) {
        setLoading(true);
      }

      try {
        const params = new URLSearchParams({
          page: pageNum.toString(),
          limit: ITEMS_PER_PAGE.toString(),
          sortField: "createdAt",
          sortOrder: "desc",
        });

        if (searchTerm.trim()) {
          params.append("search", searchTerm.trim());
        }
        if (year) {
          params.append("year", year);
        }

        const response = await axios.get(
          `${base_url}/v1/getAllProjects?${params.toString()}`
        );

        if (response.data.status === 1) {
          const fetchedProjects = response.data.data || [];
          const total = response.data.totalProjects || fetchedProjects.length;

          setTotalProjects(total);

          if (isNewSearch || pageNum === 1) {
            setProjects(fetchedProjects);
          } else {
            setProjects((prev) => [...prev, ...fetchedProjects]);
          }

          setHasMore(fetchedProjects.length === ITEMS_PER_PAGE);
          setError(null);
        } else {
          setError("Failed to retrieve projects.");
          setHasMore(false);
        }
      } catch (err) {
        console.error("Error fetching projects:", err);
        setError("Error while fetching projects.");
        setHasMore(false);
      } finally {
        setLoading(false);
        setInitialLoading(false);
      }
    },
    [searchTerm, year]
  );

  useEffect(() => {
    if (open) {
      setInitialLoading(true);
      setPage(1);
      setProjects([]);
      setHasMore(true);
      fetchProjects(1, true);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      setPage(1);
      setProjects([]);
      setHasMore(true);
      fetchProjects(1, true);
    }, 500);

    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [searchTerm, year]);

  const handleScroll = useCallback(() => {
    if (!listRef.current || loading || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = listRef.current;

    if (scrollTop + clientHeight >= scrollHeight - 100) {
      const nextPage = page + 1;
      setPage(nextPage);
      setLoading(true);
      fetchProjects(nextPage, false);
    }
  }, [loading, hasMore, page, fetchProjects]);

  useEffect(() => {
    const listElement = listRef.current;
    if (listElement) {
      listElement.addEventListener("scroll", handleScroll);
      return () => listElement.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  const handleSelect = (project) => {
    onSelect({
      anr: project.ANR,
      auftragsbezeichnung: project.Auftragsbezeichnung,
      maxLohnaufwand: project.MaxLohnaufwand || null,
      projectId: project._id,
    });
    onClose();
  };

  const handleClose = () => {
    setSearchTerm("");
    setYear(new Date().getFullYear().toString());
    setPage(1);
    setProjects([]);
    setError(null);
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-[95%] max-w-[600px] max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-xl font-semibold">Projekt auswählen</h2>
          <button
            onClick={handleClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Filters */}
        <div className="p-4 space-y-3 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="ANR oder Auftragsbezeichnung suchen..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="relative">
            <select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className="w-full px-4 py-2 border rounded-lg appearance-none focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
            >
              <option value="">Alle Jahre</option>
              {yearOptions.map((y) => (
                <option key={y} value={y.toString()}>
                  {y}
                </option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          </div>
        </div>

        {/* Results count */}
        {!initialLoading && (
          <div className="px-4 py-2 text-sm text-gray-500">
            {totalProjects > 0
              ? `${projects.length} von ${totalProjects} Projekten`
              : "Keine Projekte gefunden"}
          </div>
        )}

        {/* Project List */}
        <div
          ref={listRef}
          className="flex-1 overflow-y-auto p-4 space-y-2 min-h-[300px]"
        >
          {initialLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              <span className="ml-2 text-gray-500">
                Projekte werden geladen...
              </span>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-500">{error}</div>
          ) : projects.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              Keine Projekte gefunden
            </div>
          ) : (
            <>
              {projects.map((project, index) => (
                <button
                  key={`${project._id}-${index}`}
                  onClick={() => handleSelect(project)}
                  className="w-full p-3 border rounded-lg text-left hover:border-blue-500 hover:bg-blue-50 transition-colors"
                >
                  <div className="font-medium text-gray-900">{project.ANR}</div>
                  <div className="text-sm text-gray-500 truncate">
                    {project.Auftragsbezeichnung || "Keine Beschreibung"}
                  </div>
                </button>
              ))}

              {loading && !initialLoading && (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                </div>
              )}

              {!hasMore && projects.length > 0 && (
                <div className="text-center py-4 text-sm text-gray-400">
                  Keine weiteren Projekte
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex justify-end">
          <button
            onClick={handleClose}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50 transition-colors"
          >
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  );
};

// Copy Template Modal
export const CopyTemplateModal = ({ open, onClose, onCopy, templateName }) => {
  const [newName, setNewName] = useState("");

  useEffect(() => {
    if (open && templateName) {
      setNewName(`${templateName} (Kopie)`);
    }
  }, [open, templateName]);

  const handleCopy = () => {
    if (newName.trim() === "") {
      toast.error("Bitte geben Sie einen gültigen Namen ein.");
      return;
    }
    onCopy(newName.trim());
    setNewName("");
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-[95%] max-w-[400px]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Template kopieren</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Neuer Template-Name
          </label>
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Template-Name eingeben..."
            className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50 transition-colors"
          >
            Abbrechen
          </button>
          <button
            onClick={handleCopy}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Kopieren
          </button>
        </div>
      </div>
    </div>
  );
};

// Delete Confirmation Modal
export const DeleteConfirmModal = ({
  open,
  onClose,
  onConfirm,
  templateName,
}) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-[95%] max-w-[400px]">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-red-600">
            Template löschen
          </h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          <p className="text-gray-600">
            Sind Sie sicher, dass Sie das Template{" "}
            <span className="font-semibold">"{templateName}"</span> löschen
            möchten? Diese Aktion kann nicht rückgängig gemacht werden.
          </p>
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50 transition-colors"
          >
            Abbrechen
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Löschen
          </button>
        </div>
      </div>
    </div>
  );
};
